import "./style.css";
import { Sfx } from "./audio/sfx";
import type { KeyDeck } from "./math/keys";
import { defaultTables, hardTables, type Difficulty } from "./math/questions";
import { Engine, isMobileView } from "./game/engine";
import { loadSave, rememberBest, writeSave, type KeyMode, type Protocol, type Quality, type SaveData } from "./game/save";
import { Session, type RunConfig, type RunSummary } from "./game/session";
import { SweepSession } from "./game/sweep";
import { World } from "./game/world";

const PAD_COLORS = ["#3df0ff", "#ff2d95", "#ffb347", "#c6ff4d"];
const HOME = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"];
const MID = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
const LOW = ["z", "x", "c", "v", "b", "n", "m"];

const canvas = document.querySelector<HTMLCanvasElement>("#view")!;
const sfx = new Sfx();
let engine: Engine;
try {
  engine = new Engine(canvas);
} catch (err) {
  const panel = document.createElement("section");
  panel.className = "panel-screen";
  panel.innerHTML = `<div class="panel slim"><h2>NO GPU LOCK</h2><p class="tagline">This deck needs WebGL. Try another browser or enable hardware acceleration.</p></div>`;
  document.getElementById("app")?.append(panel);
  throw err;
}

const world = new World(engine.scene);
const intercept = new Session(engine, world, sfx);
const sweep = new SweepSession(engine, world, sfx);
intercept.setActive(true);
sweep.setActive(false);

const menu = el("#menu");
const pause = el("#pause");
const gameover = el("#gameover");
const how = el("#how");
const liveStats = el("#live-stats");
const questionDock = el("#question-dock");
const touchBar = el("#touch-bar");
const pads = el("#pads");
const kb = el("#kb");
const banner = el("#banner");
const pauseBtn = el("#btn-pause") as HTMLButtonElement;
const muteBtn = el("#btn-mute") as HTMLButtonElement;
const chips = el("#table-chips");
const keycaps = el("#keycaps");

let save: SaveData = loadSave();
engine.setQuality(save.quality);
sfx.setMuted(save.muted);
muteBtn.textContent = save.muted ? "SIG OFF" : "SIG ON";

function el(sel: string): HTMLElement {
  const node = document.querySelector<HTMLElement>(sel);
  if (!node) throw new Error(`missing ${sel}`);
  return node;
}

function persist(): void {
  writeSave(save);
}

function active() {
  return save.protocol === "sweep" ? sweep : intercept;
}

function markSeg(root: HTMLElement, attr: string, value: string): void {
  root.querySelectorAll("button").forEach((btn) => {
    btn.classList.toggle("on", btn.getAttribute(attr) === value);
  });
}

function renderChips(): void {
  chips.innerHTML = "";
  for (let n = 1; n <= 12; n++) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `chip${save.tables.includes(n) ? " on" : ""}`;
    b.textContent = String(n);
    b.addEventListener("click", () => {
      sfx.ui();
      if (save.tables.includes(n)) {
        if (save.tables.length === 1) return;
        save.tables = save.tables.filter((t) => t !== n);
      } else {
        save.tables = [...save.tables, n].sort((a, b) => a - b);
      }
      save.squaresOnly = false;
      persist();
      renderChips();
    });
    chips.append(b);
  }
}

function keyHint(): string {
  if (save.protocol === "sweep") {
    return save.keyMode === "lock"
      ? "Four pads stay on the same letters all run — hammer the facts."
      : "Four pads remap every product. Steer with pointer or arrows, fire with the highlighted key.";
  }
  return "Each incoming drone is tagged with a random key. Find it on the board and fire — no aiming, no pause.";
}

function briefing(): { title: string; steps: string[] } {
  if (save.protocol === "sweep") {
    return {
      title: "SWEEP",
      steps: [
        "Steer the skiff with pointer, finger, or arrows. Never stop moving.",
        "A live product hangs in the rain. Four pads show answers with random letters.",
        "Press that key — or tap the pad — to fire. Correct locks chain-cannons on the nearest swarm.",
        "Wrong keys and collisions burn hull. Questions never pause the city.",
        "Shuffle remaps keys every product. Lock keeps the same four keys.",
      ],
    };
  }
  return {
    title: "INTERCEPT",
    steps: [
      "Four drones close in, each tagged with an answer and a random keyboard key.",
      "Punch the matching key, or tap the drone / lane, to fire.",
      "The correct product clears the wave. A wrong product or a breach burns hull.",
      "Keys reshuffle every question so you scan the board, not a fixed lane.",
      "Pick home row, mixed, or the full deck. Cadet is slower. Ace is not.",
    ],
  };
}

function applyProtocol(): void {
  const sweepOn = save.protocol === "sweep";
  intercept.setActive(!sweepOn);
  sweep.setActive(sweepOn);
  engine.setRig(sweepOn ? "chase" : "orbit");
  document.body.dataset.protocol = save.protocol;
  el("#lock-field").hidden = !sweepOn;
  el("#streak-label").textContent = sweepOn ? "COMBO" : "STREAK";
  el("#q-kicker").textContent = sweepOn ? "LIVE PRODUCT · CHAIN FIRE" : "TARGET PRODUCT · LIVE FIRE";
  el("#btn-start").textContent = sweepOn ? "ENGAGE" : "INITIATE";
  el("#key-hint").textContent = keyHint();
}

function syncMenu(): void {
  renderChips();
  markSeg(el("#difficulty"), "data-diff", save.difficulty);
  markSeg(el("#keymap"), "data-keys", save.deck);
  markSeg(el("#keymode"), "data-lock", save.keyMode);
  markSeg(el("#quality"), "data-quality", save.quality);
  el("#protocol").querySelectorAll<HTMLButtonElement>(".mode-card").forEach((btn) => {
    btn.classList.toggle("on", btn.dataset.protocol === save.protocol);
  });
  applyProtocol();
  const best = save.best[save.protocol];
  const other = save.protocol === "intercept" ? save.best.sweep : save.best.intercept;
  const otherName = save.protocol === "intercept" ? "SWEEP" : "INTERCEPT";
  el("#hiscore-line").textContent = `BEST ${String(best).padStart(6, "0")}  ·  ${otherName} ${String(other).padStart(6, "0")}`;
}

el("#protocol").addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-protocol]");
  const v = btn?.dataset.protocol as Protocol | undefined;
  if (!v || v === save.protocol) return;
  sfx.ui();
  save.protocol = v;
  persist();
  syncMenu();
});

el("#difficulty").addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest("button");
  const v = btn?.getAttribute("data-diff") as Difficulty | null;
  if (!v) return;
  sfx.ui();
  save.difficulty = v;
  persist();
  markSeg(el("#difficulty"), "data-diff", v);
});

el("#keymap").addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest("button");
  const v = btn?.getAttribute("data-keys") as KeyDeck | null;
  if (!v) return;
  sfx.ui();
  save.deck = v;
  persist();
  markSeg(el("#keymap"), "data-keys", v);
});

el("#keymode").addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest("button");
  const v = btn?.getAttribute("data-lock") as KeyMode | null;
  if (!v) return;
  sfx.ui();
  save.keyMode = v;
  persist();
  markSeg(el("#keymode"), "data-lock", v);
  el("#key-hint").textContent = keyHint();
});

el("#quality").addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest("button");
  const v = btn?.getAttribute("data-quality") as Quality | null;
  if (!v) return;
  sfx.ui();
  save.quality = v;
  engine.setQuality(v);
  persist();
  markSeg(el("#quality"), "data-quality", v);
});

document.querySelectorAll<HTMLButtonElement>("[data-tables]").forEach((btn) => {
  btn.addEventListener("click", () => {
    sfx.ui();
    const mode = btn.dataset.tables;
    save.squaresOnly = mode === "squares";
    if (mode === "hard") save.tables = hardTables();
    else save.tables = defaultTables();
    persist();
    renderChips();
  });
});

let screenMode: "menu" | "play" | "pause" | "over" | "how" = "menu";

function shouldShowTouch(): boolean {
  return save.protocol === "intercept" && (screenMode === "play" || screenMode === "pause") && isMobileView();
}

function shouldShowPads(): boolean {
  return save.protocol === "sweep" && (screenMode === "play" || screenMode === "pause");
}

function show(screen: "menu" | "play" | "pause" | "over" | "how"): void {
  screenMode = screen;
  menu.hidden = screen !== "menu";
  pause.hidden = screen !== "pause";
  gameover.hidden = screen !== "over";
  how.hidden = screen !== "how";
  liveStats.hidden = screen !== "play" && screen !== "pause";
  questionDock.hidden = screen !== "play";
  pauseBtn.hidden = screen !== "play";
  touchBar.hidden = !shouldShowTouch();
  pads.hidden = !shouldShowPads();
  kb.hidden = !shouldShowPads();
  if (screen !== "play") {
    banner.hidden = true;
    el("#fx-frame").className = "";
  }
}

window.addEventListener("resize", () => {
  touchBar.hidden = !shouldShowTouch();
  pads.hidden = !shouldShowPads();
});

function buildKeyboard(): void {
  kb.innerHTML = "";
  for (const row of [HOME, MID, LOW]) {
    const r = document.createElement("div");
    r.className = "kb-row";
    for (const key of row) {
      const k = document.createElement("i");
      k.className = "key";
      k.dataset.key = key;
      k.textContent = key;
      r.append(k);
    }
    kb.append(r);
  }
}
buildKeyboard();

function flashBanner(text: string): void {
  banner.hidden = false;
  banner.textContent = text;
  banner.classList.remove("show");
  void banner.offsetWidth;
  banner.classList.add("show");
}

function renderHud(): void {
  const snap = active().snapshot();
  el("#stat-hull").textContent = snap.hullText;
  el("#stat-score").textContent = snap.scoreText;
  el("#stat-streak").textContent = String(snap.streak);
  el("#stat-streak").classList.toggle("hot", snap.streak >= 5);
  el("#stat-wave").textContent = String(snap.wave).padStart(2, "0");
  const qEl = el("#question-text");
  const nextPrompt = snap.question?.prompt ?? "—";
  if (qEl.textContent !== nextPrompt) {
    qEl.textContent = nextPrompt;
    qEl.classList.remove("slam");
    void qEl.offsetWidth;
    qEl.classList.add("slam");
  }
  el("#callout").textContent = snap.callout;
  document.body.dataset.score = String(snap.score);
  document.body.dataset.playing = snap.playing ? "1" : "0";
  document.body.dataset.prompt = snap.question?.prompt ?? "";
  if (!snap.question) return;

  if (save.protocol === "intercept") {
    keycaps.innerHTML = snap.question.choices
      .map(
        (c) =>
          `<div class="keycap"><span class="k">${c.key.toUpperCase()}</span><span class="n">${c.value}</span></div>`,
      )
      .join("");
    touchBar.innerHTML = snap.question.choices
      .map(
        (c) =>
          `<button type="button" class="lane-btn" data-lane="${c.lane}" data-value="${c.value}" data-key="${c.key}"><span class="key">${c.key.toUpperCase()}</span><span class="big">${c.value}</span></button>`,
      )
      .join("");
    return;
  }

  const activeKeys = new Map<string, string>();
  pads.innerHTML = "";
  snap.question.choices.forEach((c, i) => {
    const color = PAD_COLORS[i] ?? PAD_COLORS[0];
    activeKeys.set(c.key, color);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pad";
    btn.dataset.key = c.key;
    btn.dataset.value = String(c.value);
    btn.dataset.lane = String(c.lane);
    btn.style.setProperty("--pad", color);
    btn.innerHTML = `<kbd>${c.key.toUpperCase()}</kbd><span>${c.value}</span>`;
    pads.append(btn);
  });
  kb.querySelectorAll<HTMLElement>(".key").forEach((node) => {
    const color = activeKeys.get(node.dataset.key ?? "");
    node.classList.toggle("live", Boolean(color));
    if (color) node.style.setProperty("--pad", color);
    else node.style.removeProperty("--pad");
  });
}

function runConfig(): RunConfig {
  return {
    tables: [...save.tables],
    difficulty: save.difficulty,
    deck: save.deck,
    squaresOnly: save.squaresOnly,
    keyMode: save.keyMode,
  };
}

function startRun(): void {
  const config = runConfig();
  applyProtocol();
  if (save.protocol === "sweep") {
    sweep.start({ ...config, keyMode: save.keyMode }, save.best.sweep);
    flashBanner("HOLD THE GRID");
  } else {
    intercept.start(config, save.best.intercept);
  }
  show("play");
  renderHud();
}

function openHow(): void {
  const brief = briefing();
  el("#how-title").textContent = brief.title;
  el("#how-list").innerHTML = brief.steps.map((s) => `<li>${s}</li>`).join("");
  show("how");
}

el("#btn-start").addEventListener("click", async () => {
  await sfx.resume();
  sfx.startAmbient();
  sfx.ui();
  startRun();
  window.focus();
});

el("#btn-how").addEventListener("click", () => {
  sfx.ui();
  openHow();
});

el("#btn-how-close").addEventListener("click", () => {
  sfx.ui();
  show("menu");
  syncMenu();
});

el("#btn-pause").addEventListener("click", () => {
  sfx.ui();
  active().setPaused(true);
  show("pause");
});

el("#btn-resume").addEventListener("click", () => {
  sfx.ui();
  active().setPaused(false);
  show("play");
  renderHud();
});

el("#btn-abort").addEventListener("click", () => {
  sfx.ui();
  active().abort();
  show("menu");
  syncMenu();
});

el("#btn-again").addEventListener("click", async () => {
  await sfx.resume();
  sfx.startAmbient();
  sfx.ui();
  startRun();
});

el("#btn-menu").addEventListener("click", () => {
  sfx.ui();
  active().abort();
  show("menu");
  syncMenu();
});

muteBtn.addEventListener("click", () => {
  const muted = sfx.toggleMute();
  save.muted = muted;
  persist();
  muteBtn.textContent = muted ? "SIG OFF" : "SIG ON";
});

function onAnswerPress(e: Event): void {
  const target = e.target;
  if (!(target instanceof Element)) return;
  if (save.protocol === "sweep") {
    const btn = target.closest<HTMLElement>("#pads [data-key]");
    if (!btn?.dataset.key) return;
    e.preventDefault();
    sweep.answer(btn.dataset.key);
    return;
  }
  const btn = target.closest<HTMLElement>("[data-lane]");
  if (!btn) return;
  e.preventDefault();
  intercept.fireLane(Number(btn.dataset.lane));
}

document.addEventListener("pointerdown", onAnswerPress, { capture: true });
document.addEventListener("click", onAnswerPress, { capture: true });

function onWorldPointer(e: PointerEvent): void {
  if (e.pointerType === "mouse" && e.button !== 0) return;
  const snap = active().snapshot();
  if (!snap.playing || snap.paused) return;
  if (e.target instanceof Element && e.target.closest("[data-lane], #pads, button")) return;
  if (save.protocol === "sweep") {
    const r = canvas.getBoundingClientRect();
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
    sweep.setSteer(Math.max(-1, Math.min(1, nx)), Math.max(-1, Math.min(1, ny)) * 1.8);
    return;
  }
  const lane = intercept.pickLane(e.clientX, e.clientY);
  if (lane !== null) intercept.fireLane(lane);
}

canvas.addEventListener("pointerdown", (e) => {
  onWorldPointer(e);
  if (save.protocol === "sweep") {
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }
});
canvas.addEventListener("pointermove", (e) => {
  if (save.protocol !== "sweep") return;
  const snap = sweep.snapshot();
  if (!snap.playing || snap.paused) return;
  onWorldPointer(e);
});

window.addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.key === "Escape" || (save.protocol === "sweep" && e.key.toLowerCase() === "p")) {
    const snap = active().snapshot();
    if (snap.playing && !snap.paused) {
      active().setPaused(true);
      show("pause");
    } else if (snap.paused) {
      active().setPaused(false);
      show("play");
    }
    return;
  }
  if (e.key === "m" && e.ctrlKey) {
    muteBtn.click();
    return;
  }
  active().handleKey(e.key);
});

function onOver(summary: RunSummary): void {
  save = rememberBest(save, save.protocol, summary.score);
  show("over");
  const fastest = summary.fastestMs ? `${Math.round(summary.fastestMs)} ms` : "—";
  el("#go-kicker").textContent = summary.score >= save.best[save.protocol] ? "NEW ORBITAL RECORD" : "SIGNAL LOST";
  el("#go-stats").innerHTML = `
    <div>SCORE<b>${String(summary.score).padStart(6, "0")}</b></div>
    <div>BEST<b>${String(save.best[save.protocol]).padStart(6, "0")}</b></div>
    <div>ACCURACY<b>${Math.round(summary.accuracy * 100)}%</b></div>
    <div>STREAK<b>${summary.streakMax}</b></div>
    <div>FASTEST<b>${fastest}</b></div>
    <div>WAVE<b>${summary.wave}</b></div>
    <div>HITS<b>${summary.hits}</b></div>
    <div>${save.protocol === "sweep" ? "MISSES" : "BREACHES"}<b>${save.protocol === "sweep" ? summary.misses : summary.leaked}</b></div>
  `;
  const byTable = new Map<number, { hits: number; seen: number }>();
  for (const [key, m] of summary.mastery) {
    const a = Number(key.split("x")[0]);
    const cur = byTable.get(a) ?? { hits: 0, seen: 0 };
    cur.hits += m.hits;
    cur.seen += m.seen;
    byTable.set(a, cur);
  }
  el("#go-mastery").innerHTML = [...byTable.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([table, m]) => {
      const pct = m.seen ? Math.round((m.hits / m.seen) * 100) : 0;
      return `<div class="mast">${table}s<b>${pct}%</b></div>`;
    })
    .join("");
}

intercept.bind(renderHud, onOver);
sweep.bind(renderHud, onOver);

syncMenu();
show("menu");

let time = 0;
function loop(): void {
  const dt = Math.min(engine.clock.getDelta(), 0.05);
  time += dt;
  const playing = active().snapshot().playing && !active().snapshot().paused;
  let drive = save.protocol === "sweep" ? 0.7 : 0.45;
  if (save.protocol === "sweep") {
    drive = sweep.simulate(dt);
    engine.follow(playing || screenMode === "menu" ? sweep.playerX : 0, dt);
  } else {
    intercept.simulate(dt, time);
  }
  world.update(dt, time, drive);
  engine.updateCamera(time, playing);
  engine.render();
  requestAnimationFrame(loop);
}
loop();
